import { useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

/**
 * InfoTip — the ⓘ and its bubble, ported from views/partials/tips/*.
 *
 * Two things matter to the port. The bubble's show/hide is CSS (`.info-tip:hover .info-tip-content`,
 * `:focus-visible`, `display:block !important`), so the markup keeps `display:none` inline and the stylesheet
 * wins — exactly as the EJS partials do. And the anchor is the CARD (`.card:has(.info-tip)` is
 * `position: relative`), so a tip inside a card must not set `position: relative` on itself.
 */
export type InfoTipProps = {
  children: ReactNode;
  /**
   * Where the ⓘ itself sits. The EJS card tips are absolutely positioned in the card's corner
   * (`top:10; right:10`, anchored by `.card:has(.info-tip)`), while a tip inside a sentence flows with the
   * text. Rendering a card tip in flow drops a stray "i" under the value — which is exactly what happened
   * the first time this component was used on a card.
   */
  anchor?: 'inline' | 'card';
  /** Where the bubble sits relative to the ⓘ. The tips use different corners per host. */
  place?: 'below-left' | 'above-center' | 'above-right';
  /**
   * Bubble width in px. Left undefined the bubble sizes to its text, which is what the pages that do not set
   * one do (views/partials/tips/* have no width; views/tax.ejs sets 280–420 on every marker it draws). A forced
   * default made Home's and the list pane's short tips 260px wide where the EJS page draws a 93px bubble.
   */
  width?: number;
  className?: string;
  /**
   * The marker the host page draws, because the class list is part of the parity contract:
   *  - 'micro' (default): the `info-tip t-micro` partials (views/partials/tips/*);
   *  - 'tax': the tax page's own markers — `info-tip tax-icon`, whose glyph carries no trailing space;
   *  - 'tiny': the column-header marker, where the ⓘ is a 13px `span.t-tiny` and the bubble lives inside it.
   */
  icon?: 'micro' | 'tax' | 'tiny';
  /** Horizontal bubble padding in px; the page's tips use 12, the table-header and panel ones 10. */
  padX?: number;
  /** The page's short tips are `white-space: nowrap` — one line, sized to the text. */
  nowrap?: boolean;
  /**
   * Letter-spacing. Zero by default (the tips reset it), but a tip rendered INSIDE a card label inherits that
   * label's 0.5px in the EJS page — views/partials/tips/day-change.ejs never resets it — and that inheritance
   * is 6px of bubble width on a 12-character string.
   */
  letterSpacing?: string | number;
  /**
   * Extra declarations on the bubble itself. The tax page writes `text-align: left` on every tip it draws —
   * a tip inside a `num` header cell otherwise inherits right-alignment, and a right-ragged paragraph reads
   * as broken text.
   */
  contentStyle?: CSSProperties;
};

const PLACE: Record<NonNullable<InfoTipProps['place']>, CSSProperties> = {
  'below-left': { top: 22, right: 0 },
  'above-center': { bottom: 22, left: '50%', transform: 'translateX(-50%)' },
  'above-right': { bottom: 22, right: 0 },
};

export function InfoTip({ children, anchor = 'inline', place = 'below-left', width, padX = 12, nowrap, letterSpacing = 0, className, icon = 'micro', contentStyle }: InfoTipProps) {
  // A tip inside a table header sits inside a scroll container, and an absolutely positioned popup is clipped by
  // it (and pushed out of the table's box). Those tips are placed as FIXED overlays from the chip's own rect —
  // the same thing the EJS PFOverlay does — and clamped to the viewport.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const chip = useRef<HTMLSpanElement | null>(null);
  const placeAt = () => {
    if (anchor === 'card' || !chip.current) return;
    const r = chip.current.getBoundingClientRect();
    const wrap = chip.current.closest('.table-scroll')?.getBoundingClientRect() ?? null;
    const H = 130;
    // Clamped INSIDE the table's own box when the chip lives in one: a popup that escapes it is clipped by the
    // scroll container on the EJS page and lands outside the table on this one.
    const minTop = (wrap ? wrap.top : 0) + 4;
    const maxTop = (wrap ? wrap.bottom : window.innerHeight) - H - 4;
    const top = Math.max(minTop, Math.min(r.bottom + 6, maxTop));
    const minLeft = (wrap ? wrap.left : 0) + 4;
    // A bubble with no width of its own is measured, not guessed: `width` is optional and most tips size to
    // their text.
    const w = width ?? chip.current.querySelector<HTMLElement>('.info-tip-content')?.offsetWidth ?? 260;
    const maxLeft = (wrap ? wrap.right : window.innerWidth) - w - 4;
    const left = Math.max(minLeft, Math.min(r.left - 6, Math.max(minLeft, maxLeft)));
    setPos({ top, left });
  };
  const tipClass = [icon === 'micro' ? 'info-tip t-micro' : icon === 'tax' ? 'info-tip tax-icon' : 'info-tip', className]
    .filter(Boolean)
    .join(' ');
  const ring: CSSProperties = {
    display: 'inline-flex',
    ...(icon === 'tiny' ? { width: 13, height: 13 } : { width: 15, height: 15 }),
    border: '1px solid var(--muted)',
    borderRadius: '50%',
    color: 'var(--muted)',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'help',
    fontStyle: 'italic',
    // The tiny marker is a `span.t-tiny` (11px in the stylesheet) with no font-size of its own; the 15px ring
    // is the one that carries the 10px glyph inline.
    ...(icon === 'tiny' ? null : { fontSize: 10 }),
    fontFamily: 'Georgia, serif',
    userSelect: 'none',
  };
  const bubble = (
    <span
      // The tax page's tips are `tax-small` bubbles, not `t-small`: same idea, its own type scale and padding
      // (views/tax.ejs writes `info-tip-content tax-small` on every marker it draws).
      className={icon === 'micro' ? 'info-tip-content t-small' : 'info-tip-content tax-small'}
      style={{
        // NEVER 'block': views/partials/tips/* keep `display:none` inline and the stylesheet's
        // `.info-tip:hover .info-tip-content { display: block !important }` reveals it. Setting it inline when the
        // bubble was positioned left it open after the pointer had gone.
        display: 'none',
        ...(pos ? { position: 'fixed' as const, top: pos.top, left: pos.left } : { position: 'absolute' as const }),
        background: 'var(--panel)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        // The column-header marker is 8/10 in views/tax.ejs (a table cell's tip, not a card's).
        padding: `8px ${icon === 'tiny' ? 10 : padX}px`,
        fontStyle: 'normal',
        fontFamily: '-apple-system, sans-serif',
        color: 'var(--text)',
        width,
        boxShadow: '0 8px 30px var(--scrim)',
        zIndex: 50,
        whiteSpace: nowrap ? 'nowrap' : 'normal',
        textTransform: 'none',
        letterSpacing,
        ...(pos ? {} : PLACE[place]),
        ...contentStyle,
      }}
    >
      {children}
    </span>
  );
  if (icon === 'tiny') {
    // views/tax.ejs's column-header marker: the ring is a 13px `span.t-tiny` INSIDE the `.info-tip`, and the
    // bubble is the tiny span's child. The nesting is what the scan reads, so it is reproduced rather than
    // flattened into one span.
    return (
      <span
        ref={chip}
        onMouseEnter={placeAt}
        onFocus={placeAt}
        className={tipClass}
        tabIndex={0}
        role="note"
        onClick={(e) => e.stopPropagation()}
        style={{ position: anchor === 'card' ? 'absolute' : 'relative', ...(anchor === 'card' ? { top: 10, right: 10 } : null) }}
      >
        <span className="t-tiny" style={ring}>
          i{bubble}
        </span>
      </span>
    );
  }
  return (
    <span
      ref={chip}
      onMouseEnter={placeAt}
      onFocus={placeAt}
      className={tipClass}
      tabIndex={0}
      role="note"
      onClick={(e) => e.stopPropagation()}
      style={{
        ...(anchor === 'card'
          ? { position: 'absolute' as const, top: 10, right: 10 }
          : { position: 'relative' as const }),
        ...ring,
      }}
    >
      {/* The EJS t-micro tips render "i " — glyph and a space — inside the chip. The tax page's own marker
          writes the bare glyph, so its trailing space is not invented here. */}
      {icon === 'micro' ? 'i ' : 'i'}
      {bubble}
    </span>
  );
}

/** Tag — the small chip in a table cell or a heading. `.tag` owns its type and box. */
export function Tag({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={className ? `tag ${className}` : 'tag'}>{children}</span>;
}
